import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Calendar } from '../ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Calendar as CalendarIcon, Receipt, Loader2, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

export const RequestReceiptModal = ({
  open,
  onClose,
  onSubmit,
  users = [],
  isSubmitting = false
}) => {
  const [form, setForm] = useState({
    recipientIds: [],
    recipientPhone: '',
    message: 'Please upload receipts for this job. Include any purchases made for materials, supplies, or other job-related expenses.',
    dueDate: null,
    createTask: true,
    taskTitle: 'Upload receipts'
  });

  const handleSubmit = () => {
    onSubmit({
      recipient_user_ids: form.recipientIds,
      recipient_phone: form.recipientPhone || null,
      message: form.message,
      due_date: form.dueDate,
      create_task: form.createTask,
      task_title: form.taskTitle
    });
  };

  const toggleRecipient = (userId) => {
    setForm(prev => ({
      ...prev,
      recipientIds: prev.recipientIds.includes(userId)
        ? prev.recipientIds.filter(id => id !== userId)
        : [...prev.recipientIds, userId]
    }));
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Filter to show crew/ops users
  const crewUsers = users.filter(u => 
    u.role === 'crew' || u.role === 'operations' || u.role === 'admin' || u.role === 'super_admin'
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Request Receipt Upload
          </DialogTitle>
          <DialogDescription>
            Send a request to crew members or vendors to upload receipts for this job.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Recipients */}
          <div>
            <Label className="mb-2 block">Recipients</Label>
            <div className="max-h-[150px] overflow-y-auto border rounded-lg p-2 space-y-1">
              {crewUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No crew members found</p>
              ) : (
                crewUsers.map(user => (
                  <label
                    key={user.id}
                    className={cn(
                      "flex items-center gap-3 p-2 rounded cursor-pointer transition-colors",
                      form.recipientIds.includes(user.id) ? "bg-primary/10" : "hover:bg-accent"
                    )}
                  >
                    <Checkbox
                      checked={form.recipientIds.includes(user.id)}
                      onCheckedChange={() => toggleRecipient(user.id)}
                    />
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-xs">
                        {getInitials(user.name || user.first_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{user.name || `${user.first_name} ${user.last_name}`}</p>
                      <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
                    </div>
                  </label>
                ))
              )}
            </div>
            {form.recipientIds.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {form.recipientIds.length} recipient(s) selected
              </p>
            )}
          </div>

          {/* Phone Number (stub for SMS) */}
          <div>
            <Label htmlFor="phone">Phone Number (optional)</Label>
            <Input
              id="phone"
              value={form.recipientPhone}
              onChange={(e) => setForm(prev => ({ ...prev, recipientPhone: e.target.value }))}
              placeholder="+1 (555) 123-4567"
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              SMS notifications coming soon
            </p>
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={form.message}
              onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Due Date */}
          <div>
            <Label>Due Date (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal mt-1">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.dueDate ? format(new Date(form.dueDate), 'PPP') : 'Select due date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={form.dueDate ? new Date(form.dueDate) : undefined}
                  onSelect={(date) => setForm(prev => ({ ...prev, dueDate: date?.toISOString() || null }))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Create Task Checkbox */}
          <div className="flex items-start gap-3 p-3 rounded-lg border">
            <Checkbox
              id="createTask"
              checked={form.createTask}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, createTask: checked }))}
            />
            <div className="flex-1">
              <Label htmlFor="createTask" className="cursor-pointer font-medium">
                Create a task
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                A task will be created and assigned to the first recipient
              </p>
              {form.createTask && (
                <Input
                  value={form.taskTitle}
                  onChange={(e) => setForm(prev => ({ ...prev, taskTitle: e.target.value }))}
                  placeholder="Task title"
                  className="mt-2 h-8"
                />
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={form.recipientIds.length === 0 || isSubmitting}
          >
            {isSubmitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
            ) : (
              'Send Request'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
